// PrimaryNavItem — F9.6 WAVE 2F · single horizontal nav entry.
//
// Modes:
//   • kind="link"  → simple <Link to={to}>  (no dropdown)
//   • kind="mega"  → trigger button that opens a dropdown on hover + click.
//                    The dropdown panel is positioned absolutely under the
//                    trigger. A 150ms close-delay prevents flicker when the
//                    pointer crosses the gap between trigger and panel.
import React, { useRef, useState, useCallback, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import PrimaryNavMega from "./PrimaryNavMega";
import { getGroupItems } from "./primaryNavData";

const CLOSE_DELAY_MS = 150;

export default function PrimaryNavItem({ item, isAdmin, hasModule }) {
    const { t } = useTranslation("header_v3");
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const closeTimer = useRef(null);

    const scheduleClose = useCallback(() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }, []);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    useEffect(() => () => cancelClose(), [cancelClose]);
    // Close when route changes
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Hidden-on-tablet variant for secondary items
    const responsiveClass = item.responsive === "desktop" ? "hidden lg:inline-flex" : "inline-flex";

    // --- Direct link ---------------------------------------------------
    if (item.kind === "link") {
        const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
        return (
            <Link
                to={item.to}
                data-testid={`primary-nav-trigger-${item.key}`}
                className={`${responsiveClass} items-center h-9 px-3 rounded-md text-sm font-medium transition-colors no-underline ${
                    active
                        ? "text-[#FFFFFF]"
                        : "text-[#E5E5E5] hover:text-white hover:bg-white/[0.06]"
                }`}
                style={active ? { backgroundColor: "rgba(255, 255, 255,0.10)" } : undefined}
            >
                {t(item.labelKey)}
            </Link>
        );
    }

    // --- Mega trigger --------------------------------------------------
    const items = getGroupItems(item.groupKey);
    // Highlight trigger if the current path matches any item in the group
    const triggerActive = items.some(
        (sub) => location.pathname === sub.to || location.pathname.startsWith(`${sub.to}/`)
    );

    return (
        <div
            className={`${responsiveClass} relative`}
            onMouseEnter={() => { cancelClose(); setOpen(true); }}
            onMouseLeave={scheduleClose}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="menu"
                data-testid={`primary-nav-trigger-${item.key}`}
                className={`inline-flex items-center gap-1 h-9 px-3 rounded-md text-sm font-medium transition-colors ${
                    triggerActive || open
                        ? "text-[#FFFFFF]"
                        : "text-[#E5E5E5] hover:text-white hover:bg-white/[0.06]"
                }`}
                style={triggerActive || open ? { backgroundColor: "rgba(255, 255, 255,0.10)" } : undefined}
            >
                {t(item.labelKey)}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key={`primary-nav-mega-${item.key}`}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 2 }}
                        transition={{ duration: 0.14, ease: [0.16, 1, 0.3, 1] }}
                        className="absolute left-0 top-full pt-2 z-50"
                        data-testid={`primary-nav-mega-${item.key}`}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        <PrimaryNavMega
                            items={items}
                            groupKey={item.groupKey}
                            onItemClick={() => setOpen(false)}
                            isAdmin={isAdmin}
                            hasModule={hasModule}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
